<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-950 pb-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-900 dark:text-gray-100">
      <div class="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Equipment Management</h1>
          <p class="text-base text-gray-600 dark:text-gray-400 mt-2">Track and manage gym equipment inventory</p>
        </div>
        <div class="flex items-center gap-2">
          <input v-model="search" type="text" class="form-input" placeholder="Search equipment..." />
          <button class="btn btn-primary" @click="openAdd">Add Equipment</button>
        </div>
      </div>

      <!-- Equipment Statistics -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Equipment</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-gray-100">{{ equipment.length }}</p>
              </div>
              <div class="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Available</p>
                <p class="text-3xl font-bold text-green-600">{{ availableCount }}</p>
              </div>
              <div class="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Maintenance</p>
                <p class="text-3xl font-bold text-yellow-600">{{ maintenanceCount }}</p>
              </div>
              <div class="w-14 h-14 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Out of Service</p>
                <p class="text-3xl font-bold text-red-600">{{ outOfServiceCount }}</p>
              </div>
              <div class="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card dark:bg-gray-900">
        <div class="card-body">
          <div class="overflow-x-auto">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th class="table-header-cell">Equipment</th>
                  <th class="table-header-cell">Category</th>
                  <th class="table-header-cell">Status</th>
                  <th class="table-header-cell">Last Maintenance</th>
                  <th class="table-header-cell">Location</th>
                  <th class="table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr class="table-row" v-for="item in filteredEquipment" :key="item.id">
                  <td class="table-cell">
                    <div class="flex items-center">
                      <div class="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mr-3">
                        <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <div>
                        <div class="font-medium">{{ item.name }}</div>
                        <div class="text-sm text-gray-500">ID: {{ item.id }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="table-cell">{{ item.category }}</td>
                  <td class="table-cell">
                    <span :class="getStatusBadgeClass(item.status)">
                      {{ item.status }}
                    </span>
                  </td>
                  <td class="table-cell">{{ item.lastMaintenance || 'N/A' }}</td>
                  <td class="table-cell">{{ item.location }}</td>
                  <td class="table-cell">
                    <div class="flex gap-2">
                      <button class="btn btn-outline btn-sm" @click="edit(item)">Edit</button>
                      <button class="btn btn-warning btn-sm" @click="scheduleMaintenance(item)">Maintenance</button>
                      <button class="btn btn-danger btn-sm" @click="remove(item)">Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Add/Edit Equipment Modal -->
      <div v-if="open" class="modal-overlay" @click="close">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="text-lg font-semibold">{{ form.id ? 'Edit Equipment' : 'Add Equipment' }}</h3>
          </div>
          <div class="modal-body">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="form-label">Equipment Name</label>
                <input v-model="form.name" type="text" class="form-input" placeholder="Treadmill Pro X1" />
              </div>
              <div>
                <label class="form-label">Category</label>
                <select v-model="form.category" class="form-input">
                  <option value="Cardio">Cardio</option>
                  <option value="Strength">Strength</option>
                  <option value="Flexibility">Flexibility</option>
                  <option value="Accessories">Accessories</option>
                </select>
              </div>
              <div>
                <label class="form-label">Status</label>
                <select v-model="form.status" class="form-input">
                  <option value="Available">Available</option>
                  <option value="In Use">In Use</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Out of Service">Out of Service</option>
                </select>
              </div>
              <div>
                <label class="form-label">Location</label>
                <input v-model="form.location" type="text" class="form-input" placeholder="Main Floor" />
              </div>
              <div>
                <label class="form-label">Last Maintenance</label>
                <input v-model="form.lastMaintenance" type="date" class="form-input" />
              </div>
              <div>
                <label class="form-label">Purchase Date</label>
                <input v-model="form.purchaseDate" type="date" class="form-input" />
              </div>
              <div class="md:col-span-2">
                <label class="form-label">Notes</label>
                <textarea v-model="form.notes" class="form-input" rows="3" placeholder="Additional notes..."></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-outline" @click="close">Cancel</button>
            <button class="btn btn-primary" @click="save">Save</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data: () => ({
    search: '',
    open: false,
    form: {
      id: null,
      name: '',
      category: 'Cardio',
      status: 'Available',
      location: '',
      lastMaintenance: '',
      purchaseDate: '',
      notes: ''
    },
    equipment: [
      { id: 1, name: 'Treadmill Pro X1', category: 'Cardio', status: 'Available', location: 'Main Floor', lastMaintenance: '2024-01-10', purchaseDate: '2023-01-15', notes: 'High-end treadmill with incline' },
      { id: 2, name: 'Bench Press Set', category: 'Strength', status: 'Available', location: 'Weight Room', lastMaintenance: '2024-01-05', purchaseDate: '2022-08-20', notes: 'Adjustable bench with safety bars' },
      { id: 3, name: 'Yoga Mats (20)', category: 'Flexibility', status: 'Available', location: 'Studio A', lastMaintenance: '', purchaseDate: '2023-03-10', notes: 'Non-slip yoga mats' },
      { id: 4, name: 'Elliptical Trainer', category: 'Cardio', status: 'Maintenance', location: 'Main Floor', lastMaintenance: '2024-01-12', purchaseDate: '2022-11-05', notes: 'Needs belt replacement' },
      { id: 5, name: 'Dumbbell Rack', category: 'Strength', status: 'Out of Service', location: 'Weight Room', lastMaintenance: '2023-12-20', purchaseDate: '2021-06-15', notes: 'Structural damage, needs replacement' }
    ]
  }),
  computed: {
    filteredEquipment() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.equipment;
      return this.equipment.filter(item =>
        item.name.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q) ||
        item.location.toLowerCase().includes(q)
      );
    },
    availableCount() {
      return this.equipment.filter(item => item.status === 'Available').length;
    },
    maintenanceCount() {
      return this.equipment.filter(item => item.status === 'Maintenance').length;
    },
    outOfServiceCount() {
      return this.equipment.filter(item => item.status === 'Out of Service').length;
    }
  },
  methods: {
    openAdd() {
      this.form = { id: null, name: '', category: 'Cardio', status: 'Available', location: '', lastMaintenance: '', purchaseDate: '', notes: '' };
      this.open = true;
    },
    edit(item) {
      this.form = { ...item };
      this.open = true;
    },
    remove(item) {
      this.$bus.$emit('confirm:open', { title: 'Delete Equipment', message: `Delete ${item.name}?` });
      this.equipment = this.equipment.filter(r => r.id !== item.id);
      this.$bus.$emit('toast:show', { title: 'Equipment deleted', type: 'warning' });
    },
    scheduleMaintenance(item) {
      this.form = { ...item, status: 'Maintenance', lastMaintenance: new Date().toISOString().split('T')[0] };
      this.save();
    },
    close() {
      this.open = false;
    },
    save() {
      if (this.form.id) {
        const idx = this.equipment.findIndex(r => r.id === this.form.id);
        if (idx > -1) this.equipment.splice(idx, 1, { ...this.form });
        this.$bus.$emit('toast:show', { title: 'Equipment updated', type: 'success' });
      } else {
        this.equipment.push({ ...this.form, id: Date.now() });
        this.$bus.$emit('toast:show', { title: 'Equipment added', type: 'success' });
      }
      this.open = false;
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'Available':
          return 'badge badge-success';
        case 'In Use':
          return 'badge badge-info';
        case 'Maintenance':
          return 'badge badge-warning';
        case 'Out of Service':
          return 'badge badge-danger';
        default:
          return 'badge badge-info';
      }
    }
  }
}
</script>
